import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { DataService } from '../../services/data.service';
import { DataDestinationGet } from '../../models/data-destination';

export interface CleaningResultsDialogData {
  projectId: string;
  projectName: string;
  responseData: any;
}

@Component({
  selector: 'app-cleaning-results-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  template: `
    <div class="app-dialog cleaning-results-dialog">
      <div class="app-dialog__heading">
        <button mat-icon-button type="button" class="cleaning-results-dialog-close" aria-label="Close" (click)="onClose()">
          <mat-icon>close</mat-icon>
        </button>
        <div class="app-dialog__heading-copy">
          <span class="app-dialog__eyebrow">Data cleaning</span>
          <div class="app-dialog__title-row">
            <div class="app-dialog__title-copy">
              <h2 mat-dialog-title class="app-dialog__title">Cleaning Complete</h2>
              <p>Review or download the cleaned data results.</p>
            </div>
          </div>
        </div>
      </div>
    <mat-dialog-content class="app-dialog__content dialog-content">
      <div class="message-section">
        <p class="completion-message">
          Data cleaning for <strong class="project-name">{{ data.projectName }}</strong> has finished successfully.
        </p>
        <div class="info-box info-box-compact new-value-info">
          <mat-icon class="info-icon">info</mat-icon>
          <span>The cleaned data can be found in the <strong>New Value</strong> columns of the results.</span>
        </div>
        <p class="action-prompt">Choose an action below:</p>
      </div>
      
      <div class="actions-section" *ngIf="!loadingDestinations">
        <button mat-raised-button color="primary" class="action-button" (click)="downloadJSON()">
          <mat-icon class="action-icon">download</mat-icon>
          <span>Download JSON Response</span>
        </button>
        
        <button *ngIf="hasFileOutput" 
                mat-raised-button 
                class="action-button" 
                (click)="viewOutputs()">
          <mat-icon class="action-icon">folder_open</mat-icon>
          <span>View Outputs</span>
        </button>
      </div>
      
      <div class="flex-center py-xl" *ngIf="loadingDestinations">
        <mat-spinner diameter="40"></mat-spinner>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="app-dialog__actions dialog-actions">
      <button mat-button (click)="onClose()">Close</button>
    </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .cleaning-results-dialog-close {
      position: absolute !important;
      top: 12px !important;
      right: 16px !important;
      z-index: 1;
      color: var(--text-secondary);
    }

    .success-icon {
      color: var(--success-green);
      font-size: 28px !important;
      width: 28px;
      height: 28px;
    }
    
    .dialog-content {
      min-width: 450px;
    }

    .message-section {
      margin-bottom: 24px;
    }

    .completion-message {
      margin: 0 0 16px 0;
      font-size: 15px;
      line-height: 1.6;
      color: var(--text-primary);
    }

    .project-name {
      color: var(--ps-digital-blue);
      font-weight: 600;
    }

    .new-value-info {
      margin-bottom: 20px;
      border-color: var(--ps-digital-blue);
      background-color: color-mix(in srgb, var(--ps-digital-blue) 8%, transparent);
      font-size: 14px;
      color: var(--text-primary);
    }

    .info-icon {
      color: var(--ps-digital-blue);
      font-size: 20px !important;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    .action-prompt {
      margin: 0;
      font-size: 13px;
      color: var(--text-secondary);
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .actions-section {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .action-button {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 16px;
      padding: 14px 20px;
      text-align: left;
      width: 100%;
      height: auto;
      min-height: 52px;
      font-size: 15px;
      font-weight: 500;
    }

    .action-icon {
      font-size: 22px !important;
      width: 22px;
      height: 22px;
    }

    .action-button span {
      flex: 1;
    }
    
  `]
})
export class CleaningResultsDialogComponent implements OnInit {
  loadingDestinations = true;
  hasFileOutput = false;

  constructor(
    public dialogRef: MatDialogRef<CleaningResultsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CleaningResultsDialogData,
    private dataService: DataService,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Fetch data destinations to check if there's a file output
    this.dataService.getDataDestinations(this.data.projectId).subscribe({
      next: (destinations) => {
        // Check if there's an "Output to File" destination
        this.hasFileOutput = destinations.some(
          dest => dest.destinationTypeName === 'Output to File' && dest.isEnabled
        );
        this.loadingDestinations = false;
      },
      error: (error) => {
        console.error('Error fetching outputs:', error);
        this.loadingDestinations = false;
      }
    });
  }

  downloadJSON(): void {
    const jsonString = JSON.stringify(this.data.responseData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with project name and timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const projectName = this.data.projectName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'project';
    link.download = `${projectName}_cleaning_results_${timestamp}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  viewOutputs(): void {
    // Close the dialog and navigate to outputs page
    this.dialogRef.close();
    this.router.navigate(['/outputs', this.data.projectId]);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
